import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { "data-head-meta-game": "" }

import { useHead } from '@unhead/vue'
import { computed } from 'vue'
import { useGamePageStore } from '@/pages/Games/game-store'
import { storeToRefs } from 'pinia'
import { type GenerateGameHeadParams, useGamePlatforms } from '@/pages/Games/utils'
import { useConfig } from '@/composables/config'
import { MANIFEST_META_KEY } from '@/constants/general'
import { useTr } from '@/composables/use-tr'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { ROUTE_NAMES } from '@/router/constants'
import { useRouter } from 'vue-router'
import { ManifestGenerator } from '@/modules/pwa'
import { useAppStore } from '@/store/app-store'


export default /*@__PURE__*/_defineComponent({
  __name: 'HeadMetaGame',
  setup(__props) {

const tr = useTr()
const router = useRouter()
const gamePageStore = useGamePageStore()
const { game } = storeToRefs(gamePageStore)
const { getHrefLangs } = useLocalizedRouters()

const developerInfo = computed(() =>
    game.value?.developer ? tr('headMetaGame.developer', { developer: game.value?.developer }) : '',
)
const metaDescription = computed(() =>
    `${tr('headMetaGame.description', { title: game.value?.title ?? '' })} ${developerInfo.value}`.trim(),
)
const metaTitle = computed(() =>
    `${tr('headMetaGame.title', { title: game.value?.title ?? '' })} ${developerInfo.value}`.trim(),
)
const altImg = computed(() => tr('headMetaGame.alt_image', { title: game.value?.title ?? '' }))

const { VUE_APP_HOST_URL } = useConfig()
const metaURL = computed(() =>
    game.value?.hru
        ? `${VUE_APP_HOST_URL}${router.resolve({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value?.hru } }).fullPath}`
        : '',
)

const gamePlatforms = useGamePlatforms(game)

const appStore = useAppStore()
const { platform } = storeToRefs(appStore)

// eslint-disable-next-line @typescript-eslint/no-shadow
function generateHead({ game }: GenerateGameHeadParams) {
    if (!game.value) {
        return {}
    }
    return {
        title: () => metaTitle.value,
        meta: [
            { name: 'description', content: () => metaDescription.value },
            { property: 'og:title', content: () => metaTitle.value },
            { property: 'og:description', content: () => metaDescription.value },
            { property: 'og:url', content: () => metaURL.value },
            { property: 'og:image', content: () => game.value?.preview },
            { property: 'og:image:alt', content: () => altImg.value },
            {
                property: 'game:platform',
                content: () => gamePlatforms.value,
            },
            { name: 'twitter:title', content: () => metaTitle.value },
            { name: 'twitter:description', content: () => metaDescription.value },
            { name: 'twitter:image', content: () => game.value?.preview },
            { name: 'twitter:image:alt', content: () => altImg.value },
        ],
        link: [
            ...getHrefLangs({ forRoute: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value.hru as string } } }),
            ...(game.value?.hru && platform.value !== 'ios'
                ? [
                      {
                          rel: 'manifest',
                          href: `/manifest/game/${game.value.hru}.webmanifest`,
                          key: MANIFEST_META_KEY,
                      },
                  ]
                : []),
            ...(game.value?.icon
                ? [
                      {
                          rel: 'apple-touch-icon',
                          sizes: '180x180',
                          href: ManifestGenerator.getScaledImage(game.value.icon, 180, 180),
                          media: '(prefers-color-scheme:light)',
                          key: 'apple-touch-icon-light',
                      },
                      {
                          rel: 'apple-touch-icon',
                          sizes: '180x180',
                          href: ManifestGenerator.getScaledImage(game.value.icon, 180, 180),
                          media: '(prefers-color-scheme:dark)',
                          key: 'apple-touch-icon-dark',
                      },
                  ]
                : []),
        ],
    }
}

const pageMeta = computed(() => generateHead({ game }))
useHead(pageMeta)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1))
}
}

})